import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import Modal from "../../../components/agent/Modal";
import { sections as importedSections } from "../../../data/sections";
import useItineraryLimits from "../../../hooks/useItineraryLimits";
// Client-facing: do not render agent purchase/upgrade UI here

// Fallback sections in case import fails
const fallbackSections = [
  {
    id: "basicInformation",
    title: "Basic Information",
    fields: [],
  },
];

// Use imported sections or fallback
const sections = importedSections || fallbackSections;

import ModernQuestionnaireSection from "../../../components/agent/ModernQuestionnaireSection";
import "../../../styles/pages/ModernQuestionnaire.css";
import SelectionRecapModal from "../../../components/agent/SelectionRecapModal";
import RidesRecapModal from "../../../components/agent/RidesRecapModal";
import RestaurantsRecapModal from "../../../components/agent/RestaurantsRecapModal";
import ShowsRecapModal from "../../../components/agent/ShowsRecapModal";
import CharactersRecapModal from "../../../components/agent/CharactersRecapModal";
import { rideOptionsByPark } from "../../../data/rideOptionsByPark";
import { groupSelectedCharactersByCategory } from "../../../data/characters";
import { showOptionsByPark } from "../../../data/showOptionsByPark";
import { restaurantOptionsByPark } from "../../../data/diningOptionsByPark";
import { useToast } from "../../../context/ToastContext";
import { parse, format } from "date-fns";
import LoadingSpinner from "../../../components/agent/UX/LoadingSpinner";
import OptimizedProgressBar from "../../../components/agent/UX/OptimizedProgressBar";
import OptimizedButton from "../../../components/agent/UX/OptimizedButton";
import useErrorHandler from "../../../hooks/useErrorHandler";
import useOptimizedFetch from "../../../hooks/useOptimizedFetch";
import dateUtils from "../../../utils/dateUtils";
import {
  validateAdultSplit,
  validateKidSplit,
  validateAgeDropdowns,
  validateDisabilityNotes,
  validateRestDays,
  validateRideSelection,
  validateShowSelection,
  validateRestaurantSelection,
} from "../../../utils/validation";
import { validateAndSanitizeQuestionnaire } from "../../../utils/sanitization";
import { validateFormData } from "../../../utils/dataValidation";
import { addCSRFToken } from "../../../utils/csrf";


import {
  createRateLimitedSubmit,
  formatTimeRemaining,
} from "../../../utils/rateLimiter";
import { FormDataManager, submissionLock } from "../../../utils/dataPersistence";
import {
  concurrentOperationManager,
} from "../../../utils/raceConditionPrevention";
import networkErrorHandler from "../../../utils/networkErrorHandler";

// Import heavy components
import CanvaStyleFireworks from "../../../components/agent/CanvaStyleFireworks";

// Cross-browser compatibility utilities for string handling
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const safeToLowerCase = (str) => {
  if (typeof str !== 'string') return '';
  return str.toLowerCase ? str.toLowerCase() : str.replace(/[A-Z]/g, (match) =>
    String.fromCharCode(match.charCodeAt(0) + 32)
  );
};

// Polyfill for Object.assign for older browsers
if (!Object.assign) {
  Object.assign = function (target) {
    if (target == null) {
      throw new TypeError('Cannot convert undefined or null to object');
    }
    var to = Object(target);
    for (var index = 1; index < arguments.length; index++) {
      var nextSource = arguments[index];
      if (nextSource != null) {
        for (var nextKey in nextSource) {
          if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
            to[nextKey] = nextSource[nextKey];
          }
        }
      }
    }
    return to;
  };
}

// Polyfill for Array.includes for older browsers
if (!Array.prototype.includes) {
  Array.prototype.includes = function (searchElement, fromIndex) {
    if (this == null) {
      throw new TypeError('Array.prototype.includes called on null or undefined');
    }
    var O = Object(this);
    var len = parseInt(O.length) || 0;
    if (len === 0) return false;
    var n = parseInt(fromIndex) || 0;
    var k = n >= 0 ? n : Math.max(len + n, 0);
    while (k < len) {
      if (O[k] === searchElement) {
        return true;
      }
      k++;
    }
    return false;
  };
}

// Polyfill for String.includes for older browsers
if (!String.prototype.includes) {
  String.prototype.includes = function (search, start) {
    if (typeof start !== 'number') {
      start = 0;
    }
    if (start + search.length > this.length) {
      return false;
    } else {
      return this.indexOf(search, start) !== -1;
    }
  };
}

const ModernQuestionnaire = () => {
  const params = useParams();
  const { tripId, agentId, agentSlug, agencySlug } = params;
  const isAgentForm = !!agentId || !!agentSlug;

  // Set viewport meta tag for proper mobile rendering
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    } else {
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    }

    // Cleanup on unmount
    return () => {
      if (viewport) {
        viewport.content = 'width=device-width, initial-scale=1.0';
      }
    };
  }, []);

  // UUID validation function
  const isValidUUID = (str) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };
  // Use the visual toast system directly
  const toastContext = useToast();
  const toast = toastContext.toast;

  // Ref to track last section that showed a toast to prevent duplicates
  const lastToastSectionRef = useRef(null);

  // Use optimized error handling and fetch
  const { executeWithErrorHandling } = useErrorHandler();
  const { fetchWithOptimization } = useOptimizedFetch();
  const STORAGE_KEY = isAgentForm
    ? agentId
      ? `parkpro-form-agent-${agentId}`
      : agentSlug
        ? agencySlug
          ? `parkpro-form-agency-${agencySlug}-agent-${agentSlug}`
          : `parkpro-form-agent-${agentSlug}`
        : null
    : `parkpro-form-${tripId}`;
  const SUBMITTED_KEY = isAgentForm
    ? agentId
      ? `parkpro-form-agent-SUBMITTED-${agentId}`
      : agentSlug
        ? agencySlug
          ? `parkpro-form-agency-SUBMITTED-${agencySlug}-agent-${agentSlug}`
          : `parkpro-form-agent-SUBMITTED-${agentSlug}`
        : null
    : `parkpro-form-SUBMITTED-${tripId}`;

  const formManager = useMemo(
    () => new FormDataManager(STORAGE_KEY, SUBMITTED_KEY),
    [STORAGE_KEY, SUBMITTED_KEY]
  );

  const debouncedAutoSaveRef = useRef(null);
  const debouncedAutoSave = useCallback((data) => {
    if (debouncedAutoSaveRef.current) clearTimeout(debouncedAutoSaveRef.current);
    setIsSaving(true);
    debouncedAutoSaveRef.current = setTimeout(() => {
      try {
        formManager.saveData(data);
        setLastSaved(Date.now());
      } catch (saveErr) {
        console.warn('Auto-save failed:', saveErr);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  }, [formManager]);

  // Clean up old storage keys when component mounts
  useEffect(() => {
    const currentFormId = tripId || agentId || agentSlug;
    if (currentFormId) {
      FormDataManager.cleanupOldKeys(currentFormId);
    }
  }, [tripId, agentId, agentSlug]);

  // Pre-fill Basic Information (fullName, email, phoneNumber) from trip client info when opening questionnaire by trip link
  useEffect(() => {
    if (!tripId || isAgentForm) return;
    const basic = formData.basicInformation || {};
    const hasExisting = safeTrim(basic.fullName) || safeTrim(basic.email) || safeTrim(basic.phoneNumber);
    if (hasExisting) return;

    const apiUrl = import.meta.env.VITE_API_URL || "";
    const url = `${apiUrl}/api/client/trips/${tripId}`;
    let cancelled = false;

    fetch(url, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const fullName = (data.fullName && String(data.fullName).trim()) || "";
        const email = (data.email && String(data.email).trim()) || "";
        const phoneNumber = (data.phoneNumber && String(data.phoneNumber).trim()) || "";
        if (!fullName && !email && !phoneNumber) return;
        setFormData((prev) => ({
          ...prev,
          basicInformation: {
            ...(prev.basicInformation || {}),
            fullName: fullName || (prev.basicInformation?.fullName ?? ""),
            email: email || (prev.basicInformation?.email ?? ""),
            phoneNumber: phoneNumber || (prev.basicInformation?.phoneNumber ?? ""),
          },
        }));
        setBasicInfoLockedFromTrip(true);
      })
      .catch(() => {});

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only pre-fill when basic info is empty on mount; omit formData to avoid overwriting user input
  }, [tripId, isAgentForm]);

  // Performance monitoring and error boundary
  useEffect(() => {
    const startTime = performance.now();

    // Monitor component performance
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'measure' && entry.name.includes('questionnaire')) {
          console.log(`Performance: ${entry.name} took ${entry.duration}ms`);
        }
      }
    });

    try {
      observer.observe({ entryTypes: ['measure'] });
    } catch (error) {
      console.warn('Performance monitoring not supported:', error);
    }

    return () => {
      const endTime = performance.now();
      console.log(`Questionnaire component render time: ${endTime - startTime}ms`);
      observer.disconnect();
    };
  }, []);

  const [formData, setFormData] = useState(() => {
    if (typeof window === 'undefined') {
      return {
        // 1. Basic Information
        basicInformation: {
          fullName: "",
          email: "",
          phoneNumber: "",
          startDate: "",
          endDate: "",
        },

        // 2. Group Information
        groupInfo: {
          adults: 0,
          numAdultMales: "",
          numAdultFemales: "",
          kids: 0,
          numBoys: "",
          numGirls: "",
          agesOfBoys: [],
          agesOfGirls: [],
        },

        // 3. Ticket & Park Access
        parkTicketAccess: {
          ticketType: "",
          hasParkHopper: "",
          specialEvents: "",
        },

        // 4. Park Visit Plans
        parkVisitPlans: {
          parks: [],
          mkDays: "",
          hsDays: "",
          epcotDays: "",
          akDays: "",
          tlDays: "",
          bbDays: "",
          includeRestDays: "",
          restDayPlacement: "",
          restDayStyle: "",
          restDayCount: "",
          finalParkPreference: "",
        },

        // 5. Accommodation & Transportation
        accommodation: {
          transportation: "",
          stayingOnProperty: "",
          resort: "",
          otherHotel: "",
        },

        // 6. Attraction & Ride Strategy
        attractionRideStrategy: {
          lightningLane: "",
          rideStrategy: [],
          mustRides: [],
        },

        // Restaurant display preferences
        showAllRestaurants: false,

        // 7. Show & Firework Strategy
        showFireworkStrategy: {
          showStrategy: [],
          mustSeeShows: [],
          fantasmicDiningPackageInterest: "",
          fantasmicDiningPackageRestaurants: [],
        },

        // 8. Dining Preferences
        diningPreferences: {
          diningPlanInterest: "",
          restaurantStrategy: [],
          mustTryRestaurants: [],
          diningTime: [],
          allergies: [],
        },

        // 9. Time & Energy Preferences
        timeEnergy: {
          parkStartTime: [],
          breakTime: [],
        },

        // 11. Special Considerations
        specialConsiderations: {
          celebration: "",
          celebrationDetails: "",
          celebrationDate: "",
          celebrationOtherDetails: "",
          hasDisability: "",
          disabilities: "",
          additionalInfo: "",
          additionalInfoDetails: "",
        },

        // 12. Specialty/Premium Experiences
        specialtyExperiences: {
          enchantedExperiences: "",
          mustExperience: [],
        },

        // 13. Character Meet & Greets
        characterMeetGreets: {
          interestedInCharacterMeetGreets: "",
          mustMeetCharacters: [],
          mustMeetCharactersOther: "",
        },
      };
    } else {
      return formManager.loadData() || {
        // 1. Basic Information
        basicInformation: {
          fullName: "",
          email: "",
          phoneNumber: "",
          startDate: "",
          endDate: "",
        },

        // 2. Group Information
        groupInfo: {
          adults: 0,
          numAdultMales: "",
          numAdultFemales: "",
          kids: 0,
          numBoys: "",
          numGirls: "",
          agesOfBoys: [],
          agesOfGirls: [],
        },

        // 3. Ticket & Park Access
        parkTicketAccess: {
          ticketType: "",
          hasParkHopper: "",
          specialEvents: "",
        },

        // 4. Park Visit Plans
        parkVisitPlans: {
          parks: [],
          mkDays: "",
          hsDays: "",
          epcotDays: "",
          akDays: "",
          tlDays: "",
          bbDays: "",
          includeRestDays: "",
          restDayPlacement: "",
          restDayStyle: "",
          restDayCount: "",
          finalParkPreference: "",
        },

        // 5. Accommodation & Transportation
        accommodation: {
          transportation: "",
          stayingOnProperty: "",
          resort: "",
          otherHotel: "",
        },
        // 6. Attraction & Ride Strategy
        attractionRideStrategy: {
          lightningLane: "",
          rideStrategy: [],
          mustRides: [],
        },

        // Restaurant display preferences
        showAllRestaurants: false,

        // 7. Show & Firework Strategy
        showFireworkStrategy: {
          showStrategy: [],
          mustSeeShows: [],
          fantasmicDiningPackageInterest: "",
          fantasmicDiningPackageRestaurants: [],
        },

        // 8. Dining Preferences
        diningPreferences: {
          diningPlanInterest: "",
          restaurantStrategy: [],
          mustTryRestaurants: [],
          diningTime: [],
          allergies: [],
        },

        // 9. Time & Energy Preferences
        timeEnergy: {
          parkStartTime: [],
          breakTime: [],
        },

        // 11. Special Considerations
        specialConsiderations: {
          celebration: "",
          celebrationDetails: "",
          celebrationDate: "",
          hasDisability: "",
          disabilities: "",
          additionalInfo: "",
          additionalInfoDetails: "",
        },

        // 12. Specialty/Premium Experiences
        specialtyExperiences: {
          enchantedExperiences: "",
          mustExperience: [],
        },

        // 13. Character Meet & Greets
        characterMeetGreets: {
          interestedInCharacterMeetGreets: "",
          mustMeetCharacters: [],
          mustMeetCharactersOther: "",
        },
      };
    }
  });

  const [sectionIndex, setSectionIndex] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [delayedQuote, setDelayedQuote] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [recapType, setRecapType] = useState("rides");
  const [isAutoSelecting, setIsAutoSelecting] = useState(false);
  const [parkCompletionistRides, setParkCompletionistRides] = useState([]);
  const [publicAgentInfo, setPublicAgentInfo] = useState(null);
  const [publicAgencyInfo, setPublicAgencyInfo] = useState(null);
  const [publicInfoError, setPublicInfoError] = useState("");
  const [showParkHopperInfo, setShowParkHopperInfo] = useState(false);
  const [, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showGeneralWarning, setShowGeneralWarning] = useState(false);
  const [submissionStartTime, setSubmissionStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [basicInfoLockedFromTrip, setBasicInfoLockedFromTrip] = useState(false);

  // Itinerary limit checking - silent tracking only, no UI shown to clients
  const {
    usage,
    canCreate: canCreateItinerary,
    loading: limitsLoading,
    error: limitsError,
    checkCanCreate
  } = useItineraryLimits();

  // Real-time countdown effect
  useEffect(() => {
    if (submissionStartTime && loading) {
      const updateCountdown = () => {
        const elapsed = Date.now() - submissionStartTime;
        const remaining = Math.max(0, 60000 - elapsed);
        setTimeRemaining(remaining);

        if (remaining > 0) {
          setTimeout(updateCountdown, 1000); // Update every second
        }
      };

      updateCountdown();
    } else {
      setTimeRemaining(null);
    }
  }, [submissionStartTime, loading]);

  // Date formatting utility
  const formatDate = useCallback((dateString, formatString = 'MM/dd/yyyy') => {
    if (!dateString) return '';
    try {
      const date = parse(dateString, 'yyyy-MM-dd', new Date());
      return format(date, formatString);
    } catch {
      return dateString;
    }
  }, []);

  // Get formatted trip dates for display
  const getFormattedTripDates = useCallback(() => {
    const startDate = formData.basicInformation?.startDate;
    const endDate = formData.basicInformation?.endDate;

    if (!startDate || !endDate) return '';

    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);

    return `${formattedStart} - ${formattedEnd}`;
  }, [formData.basicInformation?.startDate, formData.basicInformation?.endDate, formatDate]);

  // Validation function for current section
  const validateCurrentSection = useCallback((section, formData) => {
    switch (section.id) {
      case "basicInformation":
        if (!safeTrim(formData.basicInformation?.fullName)) {
          return { isValid: false, message: "Please enter your full name" };
        }
        if (!safeTrim(formData.basicInformation?.email)) {
          return { isValid: false, message: "Please enter your email address" };
        }
        if (!formData.basicInformation?.startDate) {
          return { isValid: false, message: "Please select your trip start date" };
        }
        if (!formData.basicInformation?.endDate) {
          return { isValid: false, message: "Please select your trip end date" };
        }
        break;

      case "groupInfo": {
        if (!formData.groupInfo?.adults || formData.groupInfo.adults < 1) {
          return { isValid: false, message: "Please enter the number of adults" };
        }

        if (formData.groupInfo?.kids === undefined || formData.groupInfo?.kids === null || formData.groupInfo?.kids === "") {
          return { isValid: false, message: "Please enter the number of children (enter 0 if no children)" };
        }

        const adultSplitValidation = validateAdultSplit(formData);
        if (!adultSplitValidation) {
          return { isValid: false, message: "Number of adult males and females must equal total adults." };
        }

        if (formData.groupInfo?.kids > 0) {
          // Validate children data (now non-blocking - shows warning only)
          // Families may provide approximate values; this is acceptable
          validateKidSplit(formData);

          // Validate boys ages if there are boys
          const numBoys = parseInt(formData.groupInfo?.numBoys || "0", 10);
          if (numBoys > 0) {
            const boysValidation = validateAgeDropdowns(
              numBoys,
              formData.groupInfo?.agesOfBoys || [],
              "boy"
            );
            if (!boysValidation) {
              return { isValid: false, message: "Please select the age of each boy." };
            }
          }

          // Validate girls ages if there are girls
          const numGirls = parseInt(formData.groupInfo?.numGirls || "0", 10);
          if (numGirls > 0) {
            const girlsValidation = validateAgeDropdowns(
              numGirls,
              formData.groupInfo?.agesOfGirls || [],
              "girl"
            );
            if (!girlsValidation) {
              return { isValid: false, message: "Please select the age of each girl." };
            }
          }
        }
        break;
      }

      case "parkTicketAccess":
        if (!formData.parkTicketAccess?.ticketType) {
          return { isValid: false, message: "Please select your ticket type" };
        }
        if (!formData.parkTicketAccess?.hasParkHopper) {
          return { isValid: false, message: "Please specify if you have Park Hopper" };
        }
        if (!formData.parkTicketAccess?.specialEvents) {
          return { isValid: false, message: "Please select your special events preference" };
        }
        break;

      case "parkVisitPlans": {
        const totalParkDays = ["mkDays", "hsDays", "epcotDays", "akDays", "tlDays", "bbDays"]
          .reduce((total, field) => {
            const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
            return total + days;
          }, 0);
        if (totalParkDays === 0) {
          return { isValid: false, message: "Please select at least one park day" };
        }
        const restDaysValidation = validateRestDays(formData);
        if (!restDaysValidation.isValid) {
          return restDaysValidation;
        }
        if (!formData.parkVisitPlans?.finalParkPreference) {
          return { isValid: false, message: "Please select your final park preference" };
        }
        break;
      }

      case "accommodation":
        if (!formData.accommodation?.transportation) {
          return { isValid: false, message: "Please select your transportation method" };
        }
        if (!formData.accommodation?.stayingOnProperty) {
          return { isValid: false, message: "Please specify if you're staying on property" };
        }
        break;

      case "attractionRideStrategy": {
        if (!formData.attractionRideStrategy?.lightningLane ||
          (typeof formData.attractionRideStrategy.lightningLane === 'string' && safeTrim(formData.attractionRideStrategy.lightningLane) === "") ||
          formData.attractionRideStrategy.lightningLane === "") {
          return { isValid: false, message: "Please select your Lightning Lane preference" };
        }
        if (!formData.attractionRideStrategy?.rideStrategy || formData.attractionRideStrategy.rideStrategy.length === 0) {
          return { isValid: false, message: "Please select at least one ride strategy" };
        }

        const isParkCompletionist = formData.attractionRideStrategy.rideStrategy.includes("Park Completionist – Ride everything possible");

        // For Park Completionist, we don't need to validate mustRides since they're auto-selected
        // For other strategies, validate that at least one ride is selected
        if (!isParkCompletionist) {
          const mustRides = formData.attractionRideStrategy?.mustRides || [];
          if (mustRides.length === 0) {
            return { isValid: false, message: "Please select at least one ride" };
          }
        }
        break;
      }

      case "specialConsiderations":
        // Validate celebration question
        if (!formData.specialConsiderations?.celebration) {
          return { isValid: false, message: "Please answer whether you're celebrating anything special" };
        }
        // If celebrating, validate celebration details (dropdown)
        if (formData.specialConsiderations?.celebration === "Yes" && !formData.specialConsiderations?.celebrationDetails?.trim()) {
          return { isValid: false, message: "Please tell us what you're celebrating" };
        }
        // If they selected a celebration type, require the date
        if (formData.specialConsiderations?.celebrationDetails?.trim() && !formData.specialConsiderations?.celebrationDate?.trim()) {
          return { isValid: false, message: "Please provide the date of the celebration" };
        }
        // If they selected "Other" for celebration, require the details
        if (formData.specialConsiderations?.celebrationDetails === "Other" && !formData.specialConsiderations?.celebrationOtherDetails?.trim()) {
          return { isValid: false, message: "Please tell us what specifically you're celebrating" };
        }
        // Validate disability question
        if (!formData.specialConsiderations?.hasDisability) {
          return { isValid: false, message: "Please answer whether anyone has a disability or mobility concern" };
        }
        if (!validateDisabilityNotes(
          formData.specialConsiderations?.hasDisability,
          formData.specialConsiderations?.disabilities
        )) {
          return { isValid: false, message: "Please explain the disability or mobility concerns so we can accommodate your needs" };
        }
        // Validate additional info question
        if (!formData.specialConsiderations?.additionalInfo) {
          return { isValid: false, message: "Please answer whether there's anything else we should know" };
        }
        // If additional info is yes, validate details
        if (formData.specialConsiderations?.additionalInfo === "Yes" && !formData.specialConsiderations?.additionalInfoDetails?.trim()) {
          return { isValid: false, message: "Please tell us what additional information we should know" };
        }
        break;

      case "diningPreferences":
        if (!formData.diningPreferences?.restaurantStrategy || formData.diningPreferences.restaurantStrategy.length === 0) {
          return { isValid: false, message: "Please select at least one restaurant strategy" };
        }
        if (!formData.diningPreferences?.mustTryRestaurants || formData.diningPreferences.mustTryRestaurants.length === 0) {
          return { isValid: false, message: "Please select at least one restaurant" };
        }
        if (!formData.diningPreferences?.allergies || formData.diningPreferences.allergies.length === 0) {
          return { isValid: false, message: "Please select your food allergies (select 'None' if you have no allergies)" };
        }
        break;

      case "showFireworkStrategy": {
        if (!formData.showFireworkStrategy?.showStrategy || formData.showFireworkStrategy.showStrategy.length === 0) {
          return { isValid: false, message: "Please select at least one show strategy" };
        }

        const isNotInterested = formData.showFireworkStrategy.showStrategy.some(
          (s) => safeToLowerCase(String(s)).includes("not interested")
        );

        if (!isNotInterested) {
          if (!formData.showFireworkStrategy?.mustSeeShows || formData.showFireworkStrategy.mustSeeShows.length === 0) {
            return { isValid: false, message: "Please select at least one show or entertainment option" };
          }
          // Require Fantasmic! Dining Package answer when Fantasmic! is selected
          const mustSee = formData.showFireworkStrategy.mustSeeShows;
          const raw = Array.isArray(mustSee) ? mustSee : [];
          const hasFantasmic = raw.some((item) => {
            const name = typeof item === "string" ? item : item?.name;
            if (name == null) return false;
            const s = String(name).trim().replace(/\uFF01/g, "!");
            return s === "Fantasmic!" || s === "Fantasmic";
          });
          if (hasFantasmic) {
            const fantasmicInterest = formData.showFireworkStrategy?.fantasmicDiningPackageInterest ?? "";
            if (!fantasmicInterest || fantasmicInterest.trim() === "") {
              return { isValid: false, message: "Please answer the Fantasmic! Dining Package question below before continuing." };
            }
          }
        }
        break;
      }

      case "timeEnergy":
        if (!formData.timeEnergy?.parkStartTime || formData.timeEnergy.parkStartTime.length === 0) {
          return { isValid: false, message: "Please select your preferred park entry time" };
        }
        if (!formData.timeEnergy?.breakTime || formData.timeEnergy.breakTime.length === 0) {
          return { isValid: false, message: "Please select your break time preference" };
        }
        break;

      case "specialtyExperiences":
        if (!formData.specialtyExperiences?.enchantedExperiences ||
          (typeof formData.specialtyExperiences.enchantedExperiences === 'string' && formData.specialtyExperiences.enchantedExperiences.trim() === "") ||
          formData.specialtyExperiences.enchantedExperiences === "") {
          return { isValid: false, message: "Please answer whether you're interested in enchanted experiences" };
        }
        // If interested, validate experience selections
        if (formData.specialtyExperiences?.enchantedExperiences === "Yes" &&
          (!formData.specialtyExperiences?.mustExperience || formData.specialtyExperiences.mustExperience.length === 0)) {
          return { isValid: false, message: "Please select at least one premium experience" };
        }
        break;

      case "characterMeetGreets":
        if (!formData.characterMeetGreets?.interestedInCharacterMeetGreets) {
          return { isValid: false, message: "Please answer whether you're interested in character meet & greets" };
        }
        if (formData.characterMeetGreets.interestedInCharacterMeetGreets === "Yes") {
          const chars = formData.characterMeetGreets?.mustMeetCharacters || [];
          if (chars.length === 0) {
            return { isValid: false, message: "Please select at least one character you'd like to meet" };
          }
          if (chars.includes("Other") && !safeTrim(formData.characterMeetGreets?.mustMeetCharactersOther)) {
            return { isValid: false, message: "Please specify which character(s) you'd like to meet" };
          }
        }
        break;

      default:
        // For any section not explicitly handled, check if all fields are filled
        if (section.fields) {
          for (const field of section.fields) {
            // Skip conditional fields that depend on other fields
            if (field.dependsOn) {
              const dependentField = formData[section.id]?.[field.dependsOn.field];
              const depVal = field.dependsOn.value;
              if (depVal === "[any]") {
                if (!dependentField || (typeof dependentField === "string" && !dependentField.trim())) continue;
              } else if (typeof depVal === "string" && depVal.startsWith("[includes:") && depVal.endsWith("]")) {
                const needle = depVal.slice(9, -1);
                const arr = Array.isArray(dependentField) ? dependentField : [];
                if (!arr.includes(needle)) continue;
              } else if (dependentField !== depVal) {
                continue;
              }
            }

            // Check if field is required and empty
            const fieldValue = formData[section.id]?.[field.id];
            if (field.required !== false && (!fieldValue || (typeof fieldValue === 'string' && !fieldValue.trim()))) {
              return { isValid: false, message: `Please complete: ${field.label}` };
            }
          }
        }
        break;
    }

    return { isValid: true };
  }, []);

  // Helper function to group selections by park
  const groupByPark = useCallback((items, selectedParks, optionsByPark) => {
    const result = {};

    // Ensure all parameters are valid
    if (!Array.isArray(items)) {
      items = [];
    }
    if (!Array.isArray(selectedParks)) {
      selectedParks = [];
    }
    if (!optionsByPark || typeof optionsByPark !== "object") {
      optionsByPark = {};
    }

    selectedParks.forEach((park) => {
      const parkItems = optionsByPark[park] || [];
      const selectedInPark = parkItems
        .filter((opt) => opt && opt.name && items.includes(opt.name))
        .map((opt) => opt.name);
      if (selectedInPark.length > 0) result[park] = selectedInPark;
    });

    return result;
  }, []);

  const selectionsByPark = useMemo(() => {
    if (!formData) {
      return {};
    }

    const parks = formData.parkVisitPlans?.parks || [];
    let selectedItems = [];
    let optionsByPark = {};

    if (recapType === "attractionRideStrategy") {
      selectedItems = formData.attractionRideStrategy?.mustRides?.length
        ? formData.attractionRideStrategy.mustRides
        : (parkCompletionistRides?.length ? parkCompletionistRides : []);
      optionsByPark = rideOptionsByPark;
    } else if (recapType === "showFireworkStrategy") {
      selectedItems = formData.showFireworkStrategy?.mustSeeShows || [];
      optionsByPark = showOptionsByPark;
    } else if (recapType === "diningPreferences") {
      selectedItems = formData.diningPreferences?.mustTryRestaurants || [];
      optionsByPark = restaurantOptionsByPark;
    } else {
      // Default fallback for unknown recap types
      selectedItems = [];
      optionsByPark = {};
    }

    const result = groupByPark(selectedItems, parks, optionsByPark);
    console.log('🔍 selectionsByPark result:', result);
    return result;
  }, [formData, recapType, groupByPark, parkCompletionistRides]);

  const selectionsByCategory = useMemo(() => {
    if (recapType !== "characterMeetGreets" || !formData?.characterMeetGreets?.mustMeetCharacters) {
      return {};
    }
    return groupSelectedCharactersByCategory(formData.characterMeetGreets.mustMeetCharacters);
  }, [formData, recapType]);

  const disneyQuotes = useMemo(
    () => [
      // Section 1: Basic Information
      "All our dreams can come true, if we have the courage to pursue them. - Walt Disney",
      // Section 2: Group Information
      "Ohana means family. Family means no one gets left behind or forgotten. - Lilo & Stitch",
      // Section 3: Ticket & Park Access
      "You're braver than you believe, stronger than you seem, and smarter than you think. - Winnie the Pooh",
      // Section 4: Park Visit Plans
      "Adventure is out there! - Up",
      // Section 5: Accommodation & Transportation
      "The most fantastic, magical things can happen, and it all starts with a wish. - Pinocchio",
      // Section 6: Attraction & Ride Strategy
      "To infinity and beyond! - Toy Story",
      // Section 7: Show & Firework Strategy
      "Laughter is timeless, imagination has no age, dreams are forever. - Walt Disney",
      // Section 8: Specialty/Premium Experiences
      "You're never too old to be young. - Snow White and the Seven Dwarfs",
      // Section 9: Character Meet & Greets
      "You've got a friend in me - Toy Story",
      // Section 10: Dining Preferences
      "Hakuna Matata! - The Lion King",
      // Section 11: Time & Energy Preferences
      "When you wish upon a star, your dreams come true. - Pinocchio",
      // Section 12: Special Considerations
      "First Think, Second Believe, Third Dream, and finally dare. - Walt Disney",
    ],
    []
  );

  useEffect(() => {
    if (formManager.isSubmitted()) {
      setIsSuccess(true);
      setModalOpen(true);
      setModalMessage("success");
    }
  }, [formManager]);

  useEffect(() => {
    debouncedAutoSave(formData);
  }, [formData, debouncedAutoSave]);


  // Listen for Park Completionist events
  useEffect(() => {
    const handleParkCompletionistStart = (event) => {
      console.log('🎯 Park Completionist start event received:', event.detail);
      setIsAutoSelecting(true);
    };

    const handleParkCompletionistComplete = (event) => {
      console.log('🎯 Park Completionist completion event received:', event.detail);
      setIsAutoSelecting(false);

      // Store the selected rides for debugging
      if (event.detail.selectedRides) {
        setParkCompletionistRides(event.detail.selectedRides);
        console.log('🎯 Stored park completionist rides:', event.detail.selectedRides);
      }

      // If we're waiting to show recap modal, show it now with proper timing
      if (showRecap && recapType === "attractionRideStrategy") {
        console.log('🎯 Forcing recap modal refresh with selected rides:', event.detail.selectedRides);

        // Force re-render by closing and reopening the modal
        setTimeout(() => {
          setShowRecap(false);
          // Wait a bit longer for state to fully update
          setTimeout(() => {
            setShowRecap(true);
          }, 200);
        }, 100);
      }
    };

    const handleParkCompletionistAutoNext = (event) => {
      console.log('🎯 Park Completionist auto-next event received:', event.detail);

      // Automatically show the recap modal for the attraction ride strategy section
      if (event.detail.sectionId === 'attractionRideStrategy') {
        console.log('🎯 Auto-showing recap modal for Park Completionist');
        setRecapType('attractionRideStrategy');
        setShowRecap(true);
      }
    };

    window.addEventListener('parkCompletionistStart', handleParkCompletionistStart);
    window.addEventListener('parkCompletionistComplete', handleParkCompletionistComplete);
    window.addEventListener('parkCompletionistAutoNext', handleParkCompletionistAutoNext);

    return () => {
      window.removeEventListener('parkCompletionistStart', handleParkCompletionistStart);
      window.removeEventListener('parkCompletionistComplete', handleParkCompletionistComplete);
      window.removeEventListener('parkCompletionistAutoNext', handleParkCompletionistAutoNext);
    };
  }, [showRecap, recapType]);

  useEffect(() => {
    // Only fetch for slug-based public forms
    if (agentSlug) {
      setPublicInfoError("");
      setPublicAgentInfo(null);
      setPublicAgencyInfo(null);
      const fetchInfo = async () => {
        try {
          let res;
          if (agencySlug) {
            // Agency+/Enterprise: /api/public-questionnaire/:agencySlug/:agentSlug
            res = await fetch(
              `/api/public-questionnaire/${agencySlug}/${agentSlug}`
            );
          } else {
            // Solo Agent+: /api/public-questionnaire/agent/:agentSlug
            res = await fetch(`/api/public-questionnaire/agent/${agentSlug}`);
          }
          if (!res.ok) {
            const data = await res.json();
            setPublicInfoError(data.message || "Agent or agency not found.");
            return;
          }
          const data = await res.json();
          setPublicAgentInfo(data.agent || null);
          setPublicAgencyInfo(data.agency || null);
        } catch (fetchErr) {
          setPublicInfoError(
            networkErrorHandler.getUserFriendlyMessage?.(fetchErr) ||
            "Failed to load agent/agency info. Please try again later."
          );
        }
      };
      fetchInfo();
    }
  }, [agentSlug, agencySlug]);

  const updateNestedFormData = useCallback((currentData, fieldId, newValue) => {
    // Map field IDs to their sections
    const fieldToSection = {
      // Basic Information
      fullName: "basicInformation",
      email: "basicInformation",
      phoneNumber: "basicInformation",
      startDate: "basicInformation",
      endDate: "basicInformation",

      // Group Information
      adults: "groupInfo",
      numAdultMales: "groupInfo",
      numAdultFemales: "groupInfo",
      kids: "groupInfo",
      numBoys: "groupInfo",
      numGirls: "groupInfo",
      agesOfBoys: "groupInfo",
      agesOfGirls: "groupInfo",

      // Ticket & Park Access
      ticketType: "parkTicketAccess",
      hasParkHopper: "parkTicketAccess",
      specialEvents: "parkTicketAccess",

      // Park Selection
      parks: "parkVisitPlans",
      mkDays: "parkVisitPlans",
      hsDays: "parkVisitPlans",
      epcotDays: "parkVisitPlans",
      akDays: "parkVisitPlans",
      tlDays: "parkVisitPlans",
      bbDays: "parkVisitPlans",
      includeRestDays: "parkVisitPlans",
      restDayPlacement: "parkVisitPlans",
      restDayStyle: "parkVisitPlans",
      restDayCount: "parkVisitPlans",
      finalParkPreference: "parkVisitPlans",

      // Accommodation & Transportation
      transportation: "accommodation",
      stayingOnProperty: "accommodation",
      resort: "accommodation",
      otherHotel: "accommodation",

      // Attraction & Ride Preferences
      lightningLane: "attractionRideStrategy",
      rideStrategy: "attractionRideStrategy",
      mustRides: "attractionRideStrategy",

      // Shows & Firework Preferences
      showStrategy: "showFireworkStrategy",
      mustSeeShows: "showFireworkStrategy",
      fantasmicDiningPackageInterest: "showFireworkStrategy",
      fantasmicDiningPackageRestaurants: "showFireworkStrategy",

      // Dining Preferences
      diningPlanInterest: "diningPreferences",
      restaurantStrategy: "diningPreferences",
      mustTryRestaurants: "diningPreferences",
      diningTime: "diningPreferences",
      allergies: "diningPreferences",


      // Time & Energy Preferences
      parkStartTime: "timeEnergy",
      breakTime: "timeEnergy",

      // Special Considerations
      celebration: "specialConsiderations",
      celebrationDetails: "specialConsiderations",
      celebrationDate: "specialConsiderations",
      celebrationOtherDetails: "specialConsiderations",
      hasDisability: "specialConsiderations",
      disabilities: "specialConsiderations",
      additionalInfo: "specialConsiderations",
      additionalInfoDetails: "specialConsiderations",

      // Specialty/Premium Experiences
      enchantedExperiences: "specialtyExperiences",
      mustExperience: "specialtyExperiences",

      // Character Meet & Greets
      interestedInCharacterMeetGreets: "characterMeetGreets",
      mustMeetCharacters: "characterMeetGreets",
      mustMeetCharactersOther: "characterMeetGreets",
    };

    const section = fieldToSection[fieldId];

    if (section) {
      // Update nested section
      const updatedData = {
        ...currentData,
        [section]: {
          ...currentData[section],
          [fieldId]: newValue,
        },
      };
      return updatedData;
    } else {
      // Fallback to flat structure for unknown fields
      const updatedData = {
        ...currentData,
        [fieldId]: newValue,
      };
      return updatedData;
    }
  }, []);

  const handleChange = useCallback((fieldId, newValue) => {
    setFormData((prevData) => {
      let updatedData = updateNestedFormData(prevData, fieldId, newValue);
      // When user switches celebration to "No", clear follow-up fields so date/details disappear and state is clean
      if (fieldId === "celebration" && newValue === "No" && updatedData.specialConsiderations) {
        updatedData = {
          ...updatedData,
          specialConsiderations: {
            ...updatedData.specialConsiderations,
            celebrationDetails: "",
            celebrationDate: "",
            celebrationOtherDetails: "",
          },
        };
      }
      if (fieldId === "includeRestDays" && newValue === "No" && updatedData.parkVisitPlans) {
        updatedData = {
          ...updatedData,
          parkVisitPlans: {
            ...updatedData.parkVisitPlans,
            restDayPlacement: "",
            restDayStyle: "",
            restDayCount: "",
          },
        };
      }
      if (fieldId === "mustMeetCharacters" && updatedData.characterMeetGreets && !(Array.isArray(newValue) && newValue.includes("Other"))) {
        updatedData = {
          ...updatedData,
          characterMeetGreets: {
            ...updatedData.characterMeetGreets,
            mustMeetCharactersOther: "",
          },
        };
      }
      return updatedData;
    });
    setError("");
  }, [updateNestedFormData]);

  const handleMultiSelect = useCallback((fieldId, selectedItems) => {
    console.log('🎯 handleMultiSelect called with:', { fieldId, selectedItems, selectedItemsLength: selectedItems?.length });

    // Validate multi-select based on field type
    let validationResult = { isValid: true };

    if (fieldId.includes('mustRides')) {
      validationResult = validateRideSelection(selectedItems, formData);
    } else if (fieldId.includes('mustShows')) {
      const isNotInterested = formData.showFireworkStrategy?.showStrategy?.includes("Not Interested");
      validationResult = validateShowSelection(selectedItems, isNotInterested, formData);
    } else if (fieldId.includes('mustRestaurants')) {
      validationResult = validateRestaurantSelection(selectedItems, formData);
    }

    if (!validationResult.isValid) {
      console.log('🎯 Validation failed:', validationResult.message);
      toast.error(validationResult.message, { duration: 3000 });
      setError(validationResult.message);
      return;
    }

    const itemsToSave =
      (fieldId.includes('mustRides') && validationResult.filteredRides) ||
      (fieldId.includes('mustShows') && validationResult.filteredShows) ||
      selectedItems;
    if ((validationResult.filteredRides || validationResult.filteredShows) && validationResult.message) {
      toast.info(validationResult.message, { duration: 4000 });
    }

    console.log('🎯 Validation passed, updating form data...');
    setFormData((prevData) => {
      const updatedData = updateNestedFormData(
        prevData,
        fieldId,
        itemsToSave
      );
      console.log('🎯 Updated form data:', {
        fieldId,
        oldValue: prevData?.attractionRideStrategy?.mustRides,
        newValue: updatedData?.attractionRideStrategy?.mustRides,
        updatedDataKeys: Object.keys(updatedData || {}),
        attractionRideStrategyKeys: Object.keys(updatedData?.attractionRideStrategy || {}),
        mustRidesLength: updatedData?.attractionRideStrategy?.mustRides?.length || 0
      });
      return updatedData;
    });
    setError(""); // Clear any previous errors
  }, [formData, updateNestedFormData, toast]);

  // Define currentSection before using it in useEffect
  const currentSection = useMemo(() => {
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      return null;
    }
    const section = sections[sectionIndex] || sections[0];
    return section;
  }, [sectionIndex]);

  // Clear warning flag when leaving Park Visit Plans section or when conditions change
  useEffect(() => {
    // Calculate park days info directly to avoid dependency issues
    const totalParkDays = [
      "mkDays",
      "hsDays",
      "epcotDays",
      "akDays",
      "tlDays",
      "bbDays",
    ].reduce((total, field) => {
      const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
      return total + days;
    }, 0);

    const totalTripDays = dateUtils.calculateTripDays(
      formData.basicInformation?.startDate,
      formData.basicInformation?.endDate
    );

    const eligibleParkDays = Math.max(totalTripDays - 2, 0);
    const isAnnualPass =
      formData.parkTicketAccess?.ticketType === "Annual Pass";
    const isHopper = formData.parkTicketAccess?.hasParkHopper === "Yes";
    const allowExtraParkDays = isAnnualPass || isHopper;

    // Clear warning flag if:
    // 1. User is not on Park Visit Plans section
    // 2. User now has Park Hopper/Annual Pass (no longer needs warning)
    // 3. User is no longer over the limit
    if (
      currentSection?.id !== "parkVisitPlans" ||
      allowExtraParkDays ||
      totalParkDays <= eligibleParkDays
    ) {
      localStorage.removeItem("parkpro-shown-park-days-warning");
    }
  }, [
    currentSection?.id,
    formData.parkVisitPlans,
    formData.parkTicketAccess,
    formData.basicInformation?.startDate,
    formData.basicInformation?.endDate,
  ]);

  const handleNext = () => {
    if (!currentSection) return;

    // Validate current section before proceeding
    const sectionValidation = validateCurrentSection(currentSection, formData);
    if (!sectionValidation.isValid) {
      toast.error(sectionValidation.message, { duration: 3000 });
      setError(sectionValidation.message);
      return;
    }

    // Check for Park Hopper warning in park visit plans section
    if (currentSection.id === "parkVisitPlans") {
      // Calculate park days info directly to avoid dependency issues
      const totalParkDays = [
        "mkDays",
        "hsDays",
        "epcotDays",
        "akDays",
        "tlDays",
        "bbDays",
      ].reduce((total, field) => {
        const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
        return total + days;
      }, 0);

      const totalTripDays = (() => {
        const start = new Date(formData.basicInformation?.startDate);
        const end = new Date(formData.basicInformation?.endDate);
        if (isNaN(start) || isNaN(end)) return 0;
        const diff = (end - start) / (1000 * 60 * 60 * 24) + 1;
        return Math.round(diff);
      })();

      const eligibleParkDays = Math.max(totalTripDays - 2, 0);
      const isAnnualPass =
        formData.parkTicketAccess?.ticketType === "Annual Pass";
      const isHopper = formData.parkTicketAccess?.hasParkHopper === "Yes";
      const allowExtraParkDays = isAnnualPass || isHopper;

      // Warning for exceeding park days
      if (totalParkDays > eligibleParkDays) {
        const warningShown = localStorage.getItem(
          "parkpro-shown-park-days-warning"
        );

        if (!warningShown) {
          localStorage.setItem("parkpro-shown-park-days-warning", "true");

          if (allowExtraParkDays) {
            // Show Park Hopper info modal for users WITH Park Hopper/Annual Pass
            setShowParkHopperInfo(true);
          } else {
            // Show general warning for users WITHOUT Park Hopper/Annual Pass
            setShowGeneralWarning(true);
          }
          return;
        }
      }
    }

    // Check if this is after individual selection sections (show recap modal before advancing)
    if (
      currentSection.id === "attractionRideStrategy" ||
      currentSection.id === "showFireworkStrategy" ||
      currentSection.id === "diningPreferences" ||
      currentSection.id === "characterMeetGreets"
    ) {
      // If auto-selection is in progress, wait for it to complete
      if (isAutoSelecting) {
        console.log('⏳ Auto-selection in progress, delaying recap modal');
        // Use setTimeout to allow state to update
        setTimeout(() => {
          setRecapType(currentSection.id);
          setShowRecap(true);
        }, 100);
      } else {
        setRecapType(currentSection.id);
        setShowRecap(true);
      }
      return;
    }

    setSectionIndex((prev) => {
      if (
        typeof sections === "undefined" ||
        !Array.isArray(sections) ||
        sections.length === 0
      ) {
        return prev;
      }
      const newIndex = Math.min(prev + 1, sections.length - 1);
      return newIndex;
    });

    // Show success toast for completed section (prevent duplicates)
    if (currentSection?.id && lastToastSectionRef.current !== currentSection.id) {
      lastToastSectionRef.current = currentSection.id;
      toast.success(`${currentSection.title} completed!`, { duration: 2000 });
    }
  };

  const handlePrevious = useCallback(() => {
    if (sectionIndex > 0) setSectionIndex(sectionIndex - 1);
  }, [sectionIndex]);

  // Create rate-limited submit function with comprehensive race condition prevention
  const rateLimitedSubmit = createRateLimitedSubmit(async () => {
    const formId = `questionnaire-${tripId || agentId || agentSlug}`;

    // Use concurrent operation manager for comprehensive race condition prevention
    return await concurrentOperationManager.executeOperation(
      `submit-questionnaire-${formId}`,
      async () => {
        // Check if form is already locked
        if (!submissionLock.acquireLock(formId)) {
          throw new Error("Form is already being submitted. Please wait.");
        }

        try {
          const showSubmissionMessage = () => {
            setModalMessage("Submitting your trip info...");
            setTimeout(() => {
              const quotes = [
                "Even miracles take a little time... – Cinderella",
                "Laughter is timeless, imagination has no age, and dreams are forever.",
              ];
              const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
              setDelayedQuote(randomQuote);
            }, 2000);
          };

          showSubmissionMessage();

          const canCreate = await checkCanCreate();
          if (!canCreate && !limitsError) {
            const limitMsg = usage
              ? `You've used ${usage.monthly_itineraries || 0} of ${usage.monthly_limit || 5} itineraries this month. Please upgrade your plan to create more.`
              : "You've reached your itinerary limit. Please upgrade your plan to create more itineraries.";
            throw new Error(limitMsg);
          }

          // Validate and sanitize the form data
          const validationResult = validateFormData(formData);

          if (!validationResult.isValid) {
            // Show user-friendly validation errors
            const errorMessage = validationResult.errors.length === 1
              ? validationResult.errors[0]
              : `Please complete the following required fields:\n• ${validationResult.errors.join('\n• ')}`;
            throw new Error(errorMessage);
          }

          const sanitizedData = validateAndSanitizeQuestionnaire(formData);
          const pvp = sanitizedData.parkVisitPlans || {};
          const includeRest = pvp.includeRestDays === "Yes";
          const parkVisitPlansWithDefaults = includeRest
            ? {
              ...pvp,
              restDayPlacement: pvp.restDayPlacement?.trim() || "Middle of trip",
              restDayStyle: pvp.restDayStyle?.trim() || "Light Day (easy pace)",
              restDayCount: pvp.restDayCount?.trim() || "1",
            }
            : { ...pvp, restDayPlacement: "", restDayStyle: "", restDayCount: "" };
          const finalData = {
            ...sanitizedData,
            parkVisitPlans: parkVisitPlansWithDefaults,
            clientName: sanitizedData.basicInformation?.fullName || "",
          };
          if (import.meta.env.DEV) {
            console.log("[Questionnaire] rest/break fields", {
              includeRestDays: finalData.parkVisitPlans?.includeRestDays,
              restDayPlacement: finalData.parkVisitPlans?.restDayPlacement,
              restDayStyle: finalData.parkVisitPlans?.restDayStyle,
              restDayCount: finalData.parkVisitPlans?.restDayCount,
              breakTime: finalData.timeEnergy?.breakTime,
            });
          }

          // Lock the form to prevent multiple submissions
          formManager.lockForm();
          setSubmissionStartTime(Date.now());

          let res, data;
          const requestPayload = isAgentForm
            ? { questionnaireResponses: finalData }
            : {
              questionnaireResponses: finalData,
              ...(isValidUUID(tripId) && { tripId }),
            };

          if (isAgentForm) {
            // Submit as a new lead for the agent using optimized fetch
            res = await executeWithErrorHandling(async () => {
              return await fetchWithOptimization(
                `${import.meta.env.VITE_API_URL}/api/agents/${agentId}/leads`,
                {
                  method: "POST",
                  credentials: 'include',
                  headers: {
                    "Content-Type": "application/json",
                    ...addCSRFToken()
                  },
                  body: JSON.stringify(requestPayload),
                }
              );
            });
          } else {
            // Submit to public itinerary endpoint using optimized fetch
            res = await executeWithErrorHandling(async () => {
              return await fetchWithOptimization(
                `${import.meta.env.VITE_API_URL}/api/itineraries/public`,
                {
                  method: "POST",
                  credentials: 'include',
                  headers: {
                    "Content-Type": "application/json",
                    ...addCSRFToken()
                  },
                  body: JSON.stringify(requestPayload),
                }
              );
            });
          }

          data = await res.json();

          if (!res.ok) {
            // Provide user-friendly error messages based on common issues
            let errorMessage = "We're having trouble processing your request.";

            if (data.message) {
              if (data.message.includes("validation")) {
                errorMessage = "Please check your form information and try again. Make sure all required fields are filled out correctly.";
              } else if (data.message.includes("server")) {
                errorMessage = "Our servers are experiencing high traffic. Please try again in a few minutes.";
              } else {
                errorMessage = data.message;
              }
            } else if (res.status === 500) {
              errorMessage = "We're experiencing technical difficulties. Please try again in a few minutes or contact your travel agent for assistance.";
            } else if (res.status === 429) {
              errorMessage = "Please wait a moment before submitting again. This helps us ensure the best experience for all users.";
            }

            throw new Error(errorMessage);
          }

          setIsSuccess(true);
          setModalMessage("success");
          // Keep submitted flag so back-button returns to success view; clear form data only (no duplicate resubmit)
          formManager.markSubmitted(true);
          setSubmissionStartTime(null);
          setTimeRemaining(null);
          setLoading(false);
          toast.success("Your trip information has been submitted successfully!", { duration: 4000 });
        } catch (submitErr) {
          const errorMessage =
            networkErrorHandler.getUserFriendlyMessage?.(submitErr) ||
            submitErr?.message ||
            "We encountered an unexpected issue. Please try again or contact your travel agent for assistance.";

          setError(errorMessage);
          setModalMessage("error");
          checkCanCreate(); // Refresh limit state after error
          setLoading(false);
          throw submitErr;
        } finally {
          // Always unlock the form and clear submission time
          formManager.unlockForm();
          submissionLock.releaseLock(formId);
          setSubmissionStartTime(null);
          setTimeRemaining(null);
        }
      }
    );
  }, `questionnaire-${tripId || agentId || agentSlug}`);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Check itinerary limit before submission
      if (isAgentForm) {
        const canCreate = await checkCanCreate();
        if (!canCreate) {
          setError("You've reached your monthly itinerary limit. Please upgrade your plan or purchase additional itineraries.");
          setModalMessage("Itinerary limit exceeded. Please upgrade your plan or purchase additional itineraries.");
          setLoading(false);
          return;
        }
      }

      await rateLimitedSubmit();
    } catch (err) {
      // Handle rate limiting errors
      if (err.message.includes("Please wait")) {
        setError(err.message);
        setModalMessage("Rate limit exceeded. Please try again later.");
      }
      // Other errors are already handled in rateLimitedSubmit
    } finally {
      setLoading(false);
    }
  };

  const handleRecapConfirm = () => {
    setShowRecap(false);
    // Go to the next section
    setSectionIndex(sectionIndex + 1);
  };

  const handleRecapBack = () => {
    setShowRecap(false);
  };

  const handleParkHopperInfoClose = () => {
    setShowParkHopperInfo(false);
  };

  const progress = useMemo(() => {
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      return 0;
    }
    return ((sectionIndex + 1) / sections.length) * 100;
  }, [sectionIndex]);

  // Calculate completed sections - only mark as completed if user has actually visited and completed them
  const completedSections = useMemo(() => {
    if (!sections || !Array.isArray(sections)) return [];

    // Only mark sections as completed if they are before the current section
    // This prevents sections from being marked complete before the user reaches them
    return Array.from({ length: sectionIndex }, (_, index) => index + 1);
  }, [sectionIndex]);


  // If success, render success page only
  if (isSuccess) {
    return (
      <div className="flex fixed inset-0 z-[9999] justify-center items-center bg-black bg-opacity-75">
        {/* Success Modal */}
        <div className="relative z-[10000] p-8 mx-4 w-full max-w-md text-center bg-white rounded-2xl shadow-2xl transition-all duration-300 transform scale-100">
          <div className="flex flex-col items-center">
            {/* Success Icon */}
            <div className="flex justify-center items-center mb-6 w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-full shadow-lg">
              <svg className="w-14 h-14 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            {/* Success Message */}
            <h2 className="mb-4 text-4xl font-bold text-green-600">Success!</h2>
            <p className="mb-4 text-lg leading-relaxed text-center text-gray-700">
              Your trip info has been submitted successfully!
            </p>
            <p className="mb-6 text-sm leading-relaxed text-center text-gray-600">
              Your travel agent will be in touch soon to start planning your magical adventure.
            </p>

            {/* Next Steps */}
            <div className="flex flex-col items-center space-y-3 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <span className="text-yellow-500">✨</span>
                <span>You can safely close this page</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-blue-500">📧</span>
                <span>Check your email for confirmation</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-500">📞</span>
                <span>Your agent will contact you shortly</span>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                // Try multiple methods to close the tab/window
                try {
                  // Method 1: Standard window.close()
                  window.close();

                  // Method 2: Check if it worked, if not try alternatives
                  setTimeout(() => {
                    if (!window.closed) {
                      // Method 3: Try to redirect to blank page (works in most cases)
                      window.location.href = 'about:blank';

                      // Method 4: Alternative for some browsers
                      setTimeout(() => {
                        if (!window.closed) {
                          // Method 5: Last resort - show message
                          alert('Please close this tab manually');
                        }
                      }, 1000);
                    }
                  }, 200);
                } catch {
                  window.location.href = 'about:blank';
                }
              }}
              className="px-6 py-3 mt-6 text-sm font-medium text-white bg-blue-600 rounded-lg transition-all duration-200 hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Close Page
            </button>
          </div>
        </div>

        {/* Fireworks Effect */}
        <CanvaStyleFireworks />
      </div>
    );
  }

  return (
    <div
      className="px-3 py-4 mx-auto max-w-4xl min-h-screen modern-questionnaire-container sm:px-4 lg:px-6 sm:py-6"
      role="main"
      aria-label="Disney Trip Planning Questionnaire"
    >
      {/* Skip link for accessibility */}
      <a href="#main-content" className="modern-skip-link">
        Skip to main content
      </a>
      {/* Header with Agent Info */}
      {isAgentForm && (publicAgentInfo || publicAgencyInfo) && (
        <div className="p-3 mb-4 rounded-2xl border shadow-2xl backdrop-blur-md bg-white/95 border-white/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-600">
                  Planning with
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {publicAgencyInfo && (
                  <span className="text-sm font-semibold text-gray-900">
                    {publicAgencyInfo.name}
                  </span>
                )}
                {publicAgentInfo && (
                  <span className="text-sm text-gray-600">
                    • {publicAgentInfo.firstname} {publicAgentInfo.lastname}
                  </span>
                )}
              </div>
            </div>
            {publicInfoError && (
              <div className="flex items-center space-x-2 text-red-600">
                <span className="text-sm">{publicInfoError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Client-facing: suppress agent purchase/upgrade warnings here */}

      {/* Top Status Bar */}
      <div className="flex justify-between items-center px-4 py-2 mx-auto max-w-4xl sm:px-6 lg:px-8">
        {/* Auto-save indicator - Top Left */}
        <div className="flex items-center px-3 py-1.5 space-x-2 rounded-full border backdrop-blur-sm bg-white/10 border-white/20">
          {isSaving ? (
            <>
              <LoadingSpinner size="sm" variant="primary" />
              <span className="text-sm font-medium text-white">Saving...</span>
            </>
          ) : (
            <>
              <span className="text-sm font-medium text-white">All changes saved</span>
            </>
          )}
        </div>

        {/* Security indicator - Top Right */}
        <div className="flex items-center px-2.5 py-1 space-x-1.5 bg-green-100 rounded-full border border-green-200">
          <span className="text-xs font-medium text-green-700">Secure & Encrypted</span>
        </div>
      </div>

      {/* Main Container */}
      <div
        id="main-content"
        className="px-4 py-4 mx-auto max-w-4xl sm:px-6 lg:px-8"
        role="form"
        aria-label="Trip Planning Form"
      >

        {/* Submission timeout indicator */}
        {submissionStartTime && loading && timeRemaining !== null && (
          <div className="flex justify-center items-center mb-2">
            <div className="flex items-center px-3 py-1 space-x-2 bg-white rounded-full border border-gray-200 shadow-sm">
              <LoadingSpinner size="sm" variant="primary" />
              <span className="text-xs font-medium text-blue-700">
                Processing... {formatTimeRemaining(timeRemaining)}
              </span>
            </div>
          </div>
        )}

        {/* Progress Section */}
        <div className="mb-4">
          <div className="p-4 rounded-2xl border shadow-2xl backdrop-blur-md bg-white/95 border-white/20">
            <div className="mb-3">
              <OptimizedProgressBar
                currentSection={sectionIndex + 1}
                totalSections={sections.length}
                completedSections={completedSections}
                sectionTitles={sections.map(s => s.title)}
                showLabel={false}
                progress={progress}
                animated={true}
                className="mb-4"
              />
            </div>

            {/* Disney Quote */}
            <div className="text-center">
              <p className="text-sm italic font-medium text-yellow-600">
                {disneyQuotes &&
                  Array.isArray(disneyQuotes) &&
                  disneyQuotes.length > 0 &&
                  sectionIndex >= 0
                  ? disneyQuotes[sectionIndex % disneyQuotes.length]
                  : "Making magic happen..."}
              </p>
            </div>
          </div>
        </div>

        {/* Main Form Card */}
        <div className="p-6 rounded-2xl border shadow-2xl backdrop-blur-md transition-all duration-300 sm:p-8 lg:p-10 bg-white/95 border-white/20 hover:shadow-3xl">
          <div className="flex items-center mb-4">
            <div>
              <h2 className="mb-3 text-2xl font-bold text-gray-800 sm:text-3xl">
                Client Trip Information
              </h2>
              {getFormattedTripDates() && (
                <p className="mb-2 text-sm text-gray-600">
                  {getFormattedTripDates()}
                </p>
              )}
              <h3 className="mb-3 text-lg font-semibold text-gray-700 sm:mb-4 sm:text-xl">
                {currentSection?.title || "Loading..."}
              </h3>
            </div>
          </div>



          {/* Form Content */}
          <div className="p-4 space-y-2 rounded-2xl bg-white/95">
            {currentSection ? (
              <ModernQuestionnaireSection
                section={currentSection}
                formData={formData}
                onFieldChange={handleChange}
                onMultiSelect={handleMultiSelect}
                validationErrors={{}}
                isActive={true}
                isCompleted={completedSections.includes(sectionIndex + 1)}
                isCollapsible={false}
                sectionIndex={sectionIndex}
                totalSections={sections?.length || 0}
                basicInfoLockedFromTrip={basicInfoLockedFromTrip}
              />
            ) : (
              <div className="py-8 text-center">
                <LoadingSpinner
                  size="lg"
                  variant="primary"
                  text="Loading questionnaire sections..."
                />
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="p-4 mt-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-1 text-sm font-medium text-red-800">
                      Please Review Your Information
                    </h3>
                    <div className="text-sm text-red-700 whitespace-pre-line" role="alert">
                      {error}
                    </div>
                    {error.includes('•') && (
                      <p className="mt-2 text-xs text-red-600">
                        Please complete the missing information above and try again.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col gap-3 justify-between mt-12 sm:flex-row sm:gap-4 sm:mt-12">
              {sectionIndex > 0 && (
                <button
                  onClick={handlePrevious}
                  className="flex justify-center items-center px-6 py-3 w-full font-medium text-gray-700 bg-gray-100 rounded-lg border border-gray-300 transition-all duration-200 sm:w-auto hover:bg-gray-200 sm:px-8"
                >
                  Previous
                </button>
              )}

              {sections &&
                Array.isArray(sections) &&
                sectionIndex < sections.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="flex justify-center items-center px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg shadow-md transition-all duration-200 sm:w-auto hover:shadow-lg hover:from-blue-500 hover:to-blue-400 sm:px-8"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || limitsLoading || !canCreateItinerary}
                  className="flex justify-center items-center px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-green-600 to-green-500 rounded-lg shadow-md transition-all duration-200 sm:w-auto hover:shadow-lg hover:from-green-500 hover:to-green-400 disabled:from-gray-400 disabled:to-gray-400 sm:px-8"
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" variant="white" className="mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Trip Info
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={modalOpen}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
        isError={!!error}
        disneyQuote={delayedQuote}
      >
      </Modal>


      {/* Section-Specific Recap Modals */}
      {recapType === "attractionRideStrategy" && (
        <RidesRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Ride Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
        />
      )}

      {recapType === "diningPreferences" && (
        <RestaurantsRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Restaurant Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
          allMealRestaurantNames={new Set(Object.values(restaurantOptionsByPark).flat().map((r) => r.name))}
        />
      )}

      {recapType === "showFireworkStrategy" && (
        <ShowsRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Show Selections"
          selectionsByPark={selectionsByPark}
        />
      )}

      {recapType === "characterMeetGreets" && (
        <CharactersRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Character Meet & Greet Selections"
          selectionsByCategory={selectionsByCategory}
        />
      )}

      {/* Fallback to generic modal for unknown types */}
      {!["attractionRideStrategy", "diningPreferences", "showFireworkStrategy", "characterMeetGreets"].includes(recapType) && (
        <SelectionRecapModal
          visible={showRecap}
          onClose={handleRecapBack}
          onConfirm={handleRecapConfirm}
          title="Review Your Selections"
          selectionsByPark={selectionsByPark}
          formData={formData}
        />
      )}

      {/* Park Hopper Info Modal */}
      {showParkHopperInfo && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-xl border border-gray-300 shadow-xl">
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-bold text-blue-600 sm:text-xl">
                Just a heads up!
              </h2>
              <p className="text-xs text-gray-700 sm:text-sm">
                It looks like you've selected more park days than your total trip
                length. If you're planning to park hop or have an Annual Pass,
                that's totally fine — just continue ahead. Otherwise, you might want
                to double-check your park selections.
              </p>
              <div className="flex flex-col gap-3 justify-center mt-4 sm:flex-row">
                <OptimizedButton
                  onClick={() => {
                    setShowParkHopperInfo(false);
                    setSectionIndex(sectionIndex + 1);
                  }}
                  variant="primary"
                >
                  Sounds Good!
                </OptimizedButton>
                <OptimizedButton
                  onClick={handleParkHopperInfoClose}
                  variant="secondary"
                >
                  Go Back
                </OptimizedButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Park Days Warning Modal */}
      {showGeneralWarning && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-xl border border-gray-300 shadow-xl">
            <div className="text-center">
              <h2 className="text-lg font-bold text-blue-400 sm:text-xl">
                Park Days Exceed Trip Length
              </h2>
              <p className="mt-2 text-sm text-gray-300">
                You've selected{" "}
                {(() => {
                  // Calculate park days info directly to avoid dependency issues
                  const totalParkDays = [
                    "mkDays",
                    "hsDays",
                    "epcotDays",
                    "akDays",
                    "tlDays",
                    "bbDays",
                  ].reduce((total, field) => {
                    const days = parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0;
                    return total + days;
                  }, 0);

                  const totalTripDays = (() => {
                    const start = new Date(formData.basicInformation?.startDate);
                    const end = new Date(formData.basicInformation?.endDate);
                    if (isNaN(start) || isNaN(end)) return 0;
                    const diff = (end - start) / (1000 * 60 * 60 * 24) + 1;
                    return Math.round(diff);
                  })();

                  const eligibleParkDays = Math.max(totalTripDays - 2, 0);
                  return `${totalParkDays} park days for a ${eligibleParkDays}-day trip`;
                })()}
                . You can either review your selections or go back to enable
                Park Hopping if you want to visit multiple parks per day.
              </p>
            </div>
            <div className="flex flex-col gap-3 justify-center mt-6 sm:flex-row">
              <OptimizedButton
                onClick={() => {
                  setShowGeneralWarning(false);
                  setSectionIndex(sectionIndex + 1);
                }}
                variant="primary"
              >
                Review Selections
              </OptimizedButton>
              <OptimizedButton
                onClick={() => {
                  setShowGeneralWarning(false);
                  // Clear the warning flag so it can be re-evaluated after enabling Park Hopping
                  localStorage.removeItem("parkpro-shown-park-days-warning");
                  // Go back to the park ticket access section to enable Park Hopping
                  const parkTicketSectionIndex = sections.findIndex(
                    (section) => section.id === "parkTicketAccess"
                  );
                  if (parkTicketSectionIndex !== -1) {
                    setSectionIndex(parkTicketSectionIndex);
                  }
                }}
                variant="secondary"
              >
                Enable Park Hopping
              </OptimizedButton>
              <OptimizedButton
                onClick={() => setShowGeneralWarning(false)}
                variant="secondary"
              >
                Go Back
              </OptimizedButton>
            </div>
          </div>
        </div>
      )}

      {/* Client-facing: suppress add-on purchase modal here */}

    </div>
  );
};

export default ModernQuestionnaire;